import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { TranslationService } from '../../services/translation.service';
import { CompletionService } from '../../services/completion.service';
import { RoutineService } from '../../services/routine.service';
import { ToolCardComponent, ToolCardStatus } from '../tool-card/tool-card.component';
import { TOOLS, TOOL_BY_ID } from '../shell.models';
import { DismissedToolsService } from '../dismissed-tools.service';

interface CatalogueCard {
  id: string;
  name: string;
  desc: string;
  mins?: number;
  status: ToolCardStatus;
  /** She marked it not for me on the home strip. */
  dismissed: boolean;
}

@Component({
  selector: 'app-tools-catalogue',
  standalone: true,
  imports: [CommonModule, TranslatePipe, ToolCardComponent],
  template: `
    <div class="cat-page">
      <h1>{{ 'shell.catalogue.title' | translate }}</h1>
      <div class="tools-grid">
        <!-- A tool she declined is still HERE, and the list is where she can
             take that back (Danna 22.09). It is marked, never hidden. -->
        <app-tool-card
          *ngFor="let tool of cards"
          [toolId]="tool.id"
          [name]="tool.name"
          [desc]="tool.desc"
          [mins]="tool.mins"
          [status]="tool.status"
          [dismissed]="tool.dismissed"
          [dismissable]="tool.dismissed"
          (pick)="open($event)"
          (undo)="restore($event)"
        ></app-tool-card>
      </div>
    </div>
  `,
  styles: [
    `
      .cat-page {
        padding: 16px 8px 24px;
        font-family: var(--sans, 'Heebo', sans-serif);
      }
      h1 {
        font-size: 18px;
        font-weight: 700;
        margin: 8px 4px 16px;
        color: #1a1a2e;
      }
      /* Three to a row, so the subtitle the card now carries has somewhere to
         live (it was four when the tile was only a photo and a name). */
      .tools-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
        align-items: stretch;
      }
      @media (min-width: 1024px) {
        .tools-grid {
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: 10px;
        }
        h1 {
          font-size: 20px;
        }
      }
    `,
  ],
})
export class ToolsCatalogueComponent implements OnInit {
  private router = inject(Router);
  private translation = inject(TranslationService);
  private completionSvc = inject(CompletionService);
  private routineSvc = inject(RoutineService);
  private dismissedSvc = inject(DismissedToolsService);

  cards: CatalogueCard[] = [];

  async ngOnInit(): Promise<void> {
    // The catalogue now says the same things about a tool as home does, so a
    // woman is not told two different stories about the same tile.
    let done = new Set<string>();
    let plan = new Set<string>();
    try {
      const completions = await this.completionSvc.getRange();
      done = this.completionSvc.todayDoneSet(completions);
      const schedule = await this.routineSvc.getSchedule();
      plan = new Set(this.routineSvc.getTodayPlan(schedule));
    } catch {
      /* the catalogue must still open when the status lookup fails */
    }
    this.cards = TOOLS.map((t) => ({
      id: t.id,
      name: this.t('shell.tools.' + t.id + '.name'),
      desc: this.toolDesc(t.id),
      mins: TOOL_BY_ID[t.id]?.mins,
      status: done.has(t.id) ? 'done' : plan.has(t.id) ? 'todo' : 'none',
      dismissed: this.dismissedSvc.has(t.id),
    }));
  }

  private t(key: string): string {
    return this.translation.translate(key);
  }

  /** The same subtitle home uses, so one tool reads one way. */
  private toolDesc(id: string): string {
    const key = 'results.tools.items.' + id + '.desc';
    const value = this.t(key);
    return value && value !== key ? value : '';
  }

  /** She takes back a "not for me", from the list. */
  restore(id: string): void {
    this.dismissedSvc.restore(id);
    const card = this.cards.find((c) => c.id === id);
    if (card) card.dismissed = false;
  }

  open(id: string): void {
    this.router.navigate([
      '/' + this.translation.getCurrentLanguage() + '/app/tool/' + id,
    ]);
  }
}
